import React, { useEffect, useState } from "react";
import { Wallet, ShieldCheck, Clock3, CheckCircle2 } from "lucide-react";

const rules = [
  {
    title: "Available vs. Pending Balance",
    description: (
      <>
        Approved earnings appear in{" "}
        <strong className="font-bold text-slate-700">
          Available Balance
        </strong>
        . Amount requested for withdrawal moves to{" "}
        <strong className="font-bold text-slate-700">
          Pending
        </strong>{" "}
        to prevent double disbursements.
      </>
    ),
    icon: Wallet,
  },
  {
    title: "Minimum Withdrawal Limit",
    description: (
      <>
        The minimum payout threshold is{" "}
        <strong className="font-bold text-slate-700">
          ₹500
        </strong>
        . Users must configure a valid UPI ID or upload a QR scanner code
        before requesting withdrawal.
      </>
    ),
    icon: ShieldCheck,
  },
  {
    title: "Processing SLA",
    description: (
      <>
        Withdrawals are processed during standard business hours. Once
        transferred, a genuine banking UTR reference number is appended to
        your transaction record.
      </>
    ),
    icon: Clock3,
  },
];

function WithdrawRules() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-[#f6f8fb] text-slate-900">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden">
        {/* Animated background glows */}
        <div className="pointer-events-none absolute -left-40 top-10 h-80 w-80 animate-[withdrawFloat_8s_ease-in-out_infinite] rounded-full bg-emerald-200/30 blur-[120px]" />

        <div className="pointer-events-none absolute -right-40 top-20 h-96 w-96 animate-[withdrawFloat_10s_ease-in-out_infinite_reverse] rounded-full bg-blue-100/40 blur-[130px]" />

        <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-slate-200" />

        <div
          className={`relative mx-auto max-w-7xl px-5 pb-10 pt-12 text-center transition-all duration-1000 sm:px-6 sm:pb-14 sm:pt-16 lg:px-8 lg:pt-20 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          {/* Refrex */}
          <div className="mb-5 inline-flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#00a96d] shadow-md shadow-emerald-200">
              <span className="text-xs font-black text-white">
                R
              </span>
            </div>

            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
              Referx
            </span>
          </div>

          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
            Wallet & Withdrawals
          </p>

          <h1 className="mt-3 text-4xl font-black leading-tight tracking-[-0.05em] text-[#08111f] sm:text-5xl lg:text-6xl">
            Wallet & Withdrawal Rules
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            Understanding balances, verification checks, and automated payout
            processing.
          </p>
        </div>
      </section>

      {/* ================= RULES ================= */}
      <section className="relative">
        <div className="pointer-events-none absolute left-1/2 top-10 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-100/30 blur-[130px]" />

        <div className="relative mx-auto max-w-5xl px-5 pb-16 sm:px-6 sm:pb-20 lg:px-8">
          <div
            className={`overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_12px_45px_rgba(15,23,42,0.06)] transition-all duration-1000 ${
              loaded
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            {rules.map((rule, index) => {
              const Icon = rule.icon;

              return (
                <div
                  key={rule.title}
                  className={`group relative p-6 transition-all duration-500 hover:bg-slate-50/70 sm:p-8 lg:p-9 ${
                    index !== rules.length - 1
                      ? "border-b border-slate-100"
                      : ""
                  }`}
                  style={{
                    transitionDelay: `${index * 120}ms`,
                  }}
                >
                  {/* Animated side accent */}
                  <div className="absolute bottom-0 left-0 top-0 w-1 origin-bottom scale-y-0 bg-[#00a96d] transition-transform duration-500 group-hover:scale-y-100" />

                  <div className="flex gap-4 sm:gap-5">
                    {/* Icon */}
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-all duration-500 group-hover:scale-110 group-hover:bg-emerald-100">
                      <Icon size={20} strokeWidth={1.9} />
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg font-black tracking-[-0.025em] text-[#08111f] sm:text-xl">
                        {rule.title}
                      </h2>

                      <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-500 sm:leading-7">
                        {rule.description}
                      </p>

                      {/* Verification indicator */}
                      <div className="mt-4 flex items-center gap-2">
                        <CheckCircle2
                          size={14}
                          className="text-emerald-500"
                        />

                        <span className="text-[10px] font-semibold text-slate-400">
                          Referx withdrawal process
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Animation */}
      <style>{`
        @keyframes withdrawFloat {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(0, -18px, 0) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}

export default WithdrawRules;